import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar, SidebarContent, SidebarHeader, SidebarMenu, useSidebar } from '../../ui/sidebar';
import { menuItems } from '../../../constant/sidebar-menu';
import { SidebarMenuItemComponent } from './sidebar-menu-Item';
import { X } from 'lucide-react';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { SidebarHeaderConfig, SidebarHeaderVariant } from '../../../pages/main/main-layout';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { groupConversations } from './hooks/useChatSidebar';
import { ConversationSearch } from '../conversation-search';
import {
  archiveConversationById,
  deleteConversationById,
  updateConversationTitleById,
} from '../../../services/storage-service';
import { useToast } from '../../../hooks/use-toast';
import { CustomDialog } from '../custom-dialog/custom-dialog';
import { hasValidConversationComponentRoute } from 'pages/services/utilityService';
import { useTranslation } from 'react-i18next';
import { DebouncedInput } from '../debounced-input/debounced-input';
import { useConfig } from 'hooks/use-config';

export function LogoSidebarHeader({
  logo,
  smallLogo,
  open,
  isMobile,
  setOpenMobile,
}: SidebarHeaderConfig) {
  return (
    <div className="relative h-10 w-full">
      <img
        src={logo}
        alt="logo"
        className={`absolute left-4 top-1 h-10 w-auto max-w-full transition-all duration-300 ${
          open || isMobile ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />

      <img
        src={smallLogo}
        alt="smallLogo"
        className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-8 w-8 transition-all duration-300 ${
          open || isMobile ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      />

      {isMobile && (
        <button
          className="absolute right-4 top-1/2 transform -translate-y-1/2"
          onClick={setOpenMobile ? () => setOpenMobile(false) : undefined}
          aria-label="Close sidebar"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
}

export function IconsSidebarHeader({
  strokeWidth = 2.2,
  toggleSidebar,
  onNewChatClick,
}: SidebarHeaderConfig) {
  return (
    <div className="h-10 w-full flex items-center justify-between">
      <button
        onClick={toggleSidebar}
        className="ml-1 p-1.5 rounded-lg hover:bg-muted-foreground/10"
      >
        <Icon name={'PanelLeftDashed' as IconName} size={20} strokeWidth={strokeWidth} />
      </button>

      <div className="flex items-center">
        <ConversationSearch />
        <button
          className="ml-1 p-2 rounded-lg hover:bg-muted-foreground/10"
          onClick={onNewChatClick}
        >
          <Icon name={'SquarePen' as IconName} size={20} strokeWidth={strokeWidth} />
        </button>
      </div>
    </div>
  );
}

export function AppSidebar({ type, logo, smallLogo, strokeWidth }: SidebarHeaderConfig) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { pathname } = useLocation();
  const { setOpenMobile, open, isMobile, openMobile, toggleSidebar } = useSidebar();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationState, setConversationState] = useState<{
    id: string | null;
    type: 'delete' | 'archive' | 'rename' | null;
  }>({
    id: null,
    type: null,
  });

  const integratedMenuItems = menuItems.filter((item) => item.isIntegrated === true);
  const page = 0;
  const pageSize = 2000;
  const flatConversationList = useChatStore((state) => state.sidebarConversationsList);
  const groupedConversations = groupConversations(flatConversationList, t);
  const fetchSidebarConversations = useChatStore((state) => state.fetchSidebarConversations);
  const removeConversationFromSidebarById = useChatStore(
    (state) => state.removeConversationFromSidebarById
  );
  const updateConversationTitleInStore = useChatStore(
    (state) => state.updateConversationTitleInStore
  );
  const { configurationSettings } = useConfig();

  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isRenameLoading, setIsRenameLoading] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  useEffect(() => {
    fetchSidebarConversations(configurationSettings, { page, pageSize });
  }, [fetchSidebarConversations]);

  const handleRename = async (conversationId: string) => {
    try {
      setIsRenameLoading(true);
      await updateConversationTitleById(conversationId, newTitle, configurationSettings);
      updateConversationTitleInStore(conversationId, newTitle);
      setIsRenameDialogOpen(false);
      setIsRenameLoading(false);
      navigate('/chat');
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('RENAME_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsRenameDialogOpen(false);
      setIsRenameLoading(false);
      toast({
        variant: 'destructive',
        title: t('RENAME_FAILED'),
        description: t('RENAME_FAILED_DESCRIPTION'),
      });
      console.error('Error renaming conversation:', error);
    }
  };

  const handleArchive = async (conversationId: string) => {
    try {
      setIsLoading(true);
      await archiveConversationById(conversationId, configurationSettings);
      removeConversationFromSidebarById(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      navigate('/chat');
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('ARCHIVE_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('ARCHIVE_FAILED'),
        description: t('ARCHIVE_FAILED_DESCRIPTION'),
      });
      console.error('Error archiving conversation:', error);
    }
  };

  const handleDelete = async (conversationId: string) => {
    try {
      setIsLoading(true);
      await deleteConversationById(conversationId, configurationSettings);
      removeConversationFromSidebarById(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      navigate('/chat');
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('DELETE_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('DELETE_FAILED'),
        description: t('DELETE_FAILED_DESCRIPTION'),
      });
      console.error('Error deleting conversation:', error);
    }
  };

  const handleConfirmAction = () => {
    if (conversationState.id && conversationState.type === 'archive') {
      handleArchive(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'delete') {
      handleDelete(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'rename') {
      handleRename(conversationState.id);
    }
  };

  const onNewChatClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
    navigate('/chat');
  };

  useEffect(() => {
    if (!isMobile) {
      setOpenMobile(false);
    }
  }, [pathname, setOpenMobile, isMobile]);

  const sidebarStyle = isMobile
    ? ({
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100%',
        zIndex: 50,
        borderRight: 'none',
        transition: 'transform 0.3s ease-in-out',
        transform: openMobile ? 'translateX(0)' : 'translateX(-100%)',
      } as React.CSSProperties)
    : ({
        width: open ? 'var(--sidebar-width)' : '64px',
        minWidth: open ? 'var(--sidebar-width)' : '64px',
        transition: 'width 0.3s ease, min-width 0.3s ease',
        height: '100%',
        borderRight: '1px solid var(--border-color, #e2e8f0)',
      } as React.CSSProperties);

  if (!open) {
    return null;
  }

  return (
    <Sidebar
      className={`bg-card h-full ${isMobile ? 'mobile-sidebar' : ''}`}
      collapsible={isMobile ? 'none' : 'icon'}
      style={sidebarStyle}
    >
      <SidebarHeader className="p-2">
        {type === SidebarHeaderVariant.Logo && (
          <LogoSidebarHeader
            type={type}
            logo={logo}
            smallLogo={smallLogo}
            open={open}
            isMobile={isMobile}
            setOpenMobile={setOpenMobile}
          />
        )}

        {type === SidebarHeaderVariant.Icons && (
          <IconsSidebarHeader
            type={type}
            strokeWidth={strokeWidth}
            toggleSidebar={toggleSidebar}
            onNewChatClick={onNewChatClick}
          />
        )}
      </SidebarHeader>

      <SidebarContent className="text-base ml-3 mr-2 my-3 text-high-emphasis font-normal">
        {integratedMenuItems.map((item) => (
          <SidebarMenu key={item.id} className="w-full font-medium">
            <SidebarMenuItemComponent
              item={item}
              showText={open || isMobile}
              isActive={pathname.includes(item.path)}
              onClick={isMobile ? () => setOpenMobile(false) : undefined}
            />
          </SidebarMenu>
        ))}

        {hasValidConversationComponentRoute(pathname) &&
          groupedConversations &&
          groupedConversations.length > 0 && (
            <div className="flex flex-col w-full gap-6 mt-7">
              {groupedConversations.map(
                (group) =>
                  group.items &&
                  group.items.length > 0 && (
                    <div key={group.id}>
                      <div className="my-1 w-full px-2 mb-4">
                        <p className="text-neutral-400 text-xs font-normal leading-none">
                          {group.label}
                        </p>
                      </div>
                      {group.items.map((item) => {
                        const itemRightIcon = {
                          iconName: 'Ellipsis' as IconName,
                          options: [
                            {
                              id: 'rename',
                              name: t('RENAME'),
                              icon: 'Pencil' as IconName,
                              callback: () => {
                                console.log('Rename Conversation:', item.ConversationId);
                                setIsRenameDialogOpen(true);
                                setNewTitle(item.ConversationTitle || '');
                                setConversationState({
                                  id: item.ConversationId,
                                  type: 'rename',
                                });
                              },
                            },
                            {
                              id: 'archive',
                              name: t('ARCHIVE'),
                              icon: 'Archive' as IconName,
                              callback: () => {
                                console.log('Archive Conversation:', item.ConversationId);
                                setIsConfirmDialogOpen(true);
                                setConversationState({
                                  id: item.ConversationId,
                                  type: 'archive',
                                });
                              },
                            },
                            {
                              id: 'delete',
                              name: t('DELETE'),
                              icon: 'Trash2' as IconName,
                              callback: () => {
                                console.log('Delete Conversation:', item.ConversationId);
                                setIsConfirmDialogOpen(true);
                                setConversationState({
                                  id: item.ConversationId,
                                  type: 'delete',
                                });
                              },
                            },
                          ],
                        };

                        return (
                          <SidebarMenu key={item.ConversationId} className="w-full font-medium">
                            <SidebarMenuItemComponent
                              item={{
                                id: item.ConversationId,
                                name: item.ConversationTitle || item.ConversationId,
                                path: `/chat/${item.ConversationId}`,
                                rightIcon: itemRightIcon,
                              }}
                              showText={open || isMobile}
                              isActive={pathname.includes(item.ConversationId)}
                              onClick={isMobile ? () => setOpenMobile(false) : undefined}
                            />
                          </SidebarMenu>
                        );
                      })}
                    </div>
                  )
              )}

              <CustomDialog
                isOpen={isConfirmDialogOpen}
                isLoading={isLoading}
                setIsOpen={setIsConfirmDialogOpen}
                title={t('CONFIRM_ACTION')}
                onConfirm={handleConfirmAction}
                confirmText={t('YES')}
                cancelText={t('CANCEL')}
                description={''}
              >
                <p>{t('CONFIRM_ACTION_DESCRIPTION')}</p>
              </CustomDialog>

              <CustomDialog
                isOpen={isRenameDialogOpen}
                isLoading={isRenameLoading}
                setIsOpen={setIsRenameDialogOpen}
                title={t('RENAME')}
                onConfirm={handleConfirmAction}
                confirmText={t('SAVE')}
                cancelText={t('CANCEL')}
                description={''}
                isConfirmDisabled={newTitle.trim() === ''}
              >
                <DebouncedInput
                  placeholder={t('ENTER_NEW_TITLE')}
                  initialValue={newTitle}
                  onDebouncedChange={setNewTitle}
                />
              </CustomDialog>
            </div>
          )}
      </SidebarContent>
    </Sidebar>
  );
}
